// Implements: F-IDN-012, F-UIX-009, F-UIX-010
/**
 * The Perfil section: owns `GET /auth/me`, the name save and the photo, and hands the form plain
 * data; "Dónde participas" reads the session's own scopes (`GET /auth/me/scopes`, no extra call). The form is keyed by the person's id (checklist 14), so a draft never leaks from one
 * account into another in the same tab.
 *
 * A refused name (`422`) is said under the field with the server's own message; a photo over
 * 2 MiB is refused before any request (`AVATAR_TOO_LARGE`), any other refusal of the photo with
 * one notice. Neither the photo nor the name is ever logged.
 */

import { useCallback, useState } from "react";
import type { ReactElement } from "react";

import { useSession } from "../../../shared/api/session";
import { useTranslation } from "../../../shared/i18n";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import { useToast } from "../../../shared/ui/molecules/ToastProvider";
import { AVATAR_TOO_LARGE } from "../api/accountApi";
import { AvatarEditor } from "../components/AvatarEditor";
import { ParticipationList } from "../components/ParticipationList";
import { ProfileForm } from "../components/ProfileForm";
import { useAvatar } from "../hooks/useAvatar";
import { useMeQuery } from "../hooks/useMe";
import { useUpdateProfile } from "../hooks/useUpdateProfile";

export const TOTP_SECTION_HREF = "/cuenta/2fa";

export function ProfileContainer(): ReactElement {
  const { t } = useTranslation();
  const me = useMeQuery();
  // "Dónde participas" (owner 2026-10-19): the person's own scopes, already in the session.
  const { state: session } = useSession();
  const update = useUpdateProfile();
  const { upload, remove } = useAvatar();
  const { push } = useToast();
  const [nameError, setNameError] = useState<string | null>(null);

  const save = useCallback(
    async (fullName: string): Promise<boolean> => {
      setNameError(null);
      try {
        await update.mutateAsync(fullName);
        push({ tone: "success", message: t("account.profile.saved") });
        return true;
      } catch (cause) {
        const failure = cause as { readonly status?: unknown; readonly message?: unknown };
        if (failure.status === 422 && typeof failure.message === "string") {
          setNameError(failure.message);
        } else {
          push({ tone: "danger", message: t("common.state.error") });
        }
        return false;
      }
    },
    [update, push, t],
  );

  const pick = useCallback(
    (file: File): void => {
      upload.mutate(file, {
        onSuccess: () => {
          push({ tone: "success", message: t("account.profile.photoUpdated") });
        },
        onError: (error) => {
          const tooLarge = error.code === AVATAR_TOO_LARGE;
          push({
            tone: "danger",
            message: t(tooLarge ? "account.profile.photoTooLarge" : "account.profile.photoRefused"),
          });
        },
      });
    },
    [upload, push, t],
  );

  const removePhoto = useCallback((): void => {
    remove.mutate(null, {
      onSuccess: () => {
        push({ tone: "success", message: t("account.profile.photoRemoved") });
      },
      onError: () => {
        push({ tone: "danger", message: t("common.state.error") });
      },
    });
  }, [remove, push, t]);

  if (me.isPending) return <LoadingState label={t("common.state.loading")} />;
  if (me.isError) {
    return (
      <ErrorState
        error={me.error}
        title={t("account.profile.loadError")}
        onRetry={() => {
          void me.refetch();
        }}
      />
    );
  }

  const profile = me.data;
  return (
    <ProfileForm
      key={profile.id}
      me={profile}
      saving={update.isPending}
      serverError={nameError}
      onSave={save}
      totpHref={TOTP_SECTION_HREF}
      participation={
        session.status === "authenticated" ? <ParticipationList scopes={session.view.scopes} /> : null
      }
      avatar={
        <AvatarEditor
          name={profile.full_name}
          imageUrl={profile.avatar_url}
          busy={upload.isPending || remove.isPending}
          onPick={pick}
          onRemove={removePhoto}
        />
      }
    />
  );
}
